// composite types

export interface ServerInfo {
    version: string;
}

export interface Doc {
    text: string;
}

export interface InviteCandidates {
    users: UserAvatar[];
    groups: GroupAvatar[];
}

export interface EventFollowRequest {
    follow?: boolean,
    attending?: boolean,
    not_attending?: boolean,
}

// auth types

export interface Session {
    session_id: string;
    user_id: number;
    username: string;
}

export interface MyAccount {
    avatar: UserAvatar;
    avatar_id: string | null;
    avatar_original_url: string | null;
    email: string | null;
    timezone: string;
    pending_emails: string[];
    friend_ids: number[];
}

export type Activity =
    "unspecified" |
    "invited" |
    "updated" |
    "temporal" |
    "location" |
    "permissions" |
    "follows" |
    "attends" |
    "solicits";

export interface ActivityRef {
    event_id: string | null,
    group_id: string | null,
}

export interface AggregateActivity {
    reference: ActivityRef;
    event: EventHappening | null;
    followers: UserAvatar[];
    attendees: UserAvatar[];
    solicitors: UserAvatar[];
    activities: Activity[];
    activity_at: string;
}

export interface ServerSideNotification {
    "eventlista.url": string;
}

export function aggregateActivityKey(a: AggregateActivity) {
    if (a.reference.event_id) {
        return 'event:' + a.reference.event_id;
    } else if (a.reference.group_id) {
        return 'group:' + a.reference.group_id;
    } else {
        return '';
    }
}

export interface BackendError extends Error {
    status: number,
}

export class LoginRequest {
    username!: string;
    password!: string;
}

export interface CreateUserRequest {
    username: string;
    password: string;
    email: string;
    timezone: string;
}

export class PasswordResetRequest {
    username?: string;
    password?: string;
    password_reset_id?: string;
    password_reset_token?: string;
}

export interface FCMTokenRegisterRequest {
    token: string;
}

export interface FCMTokenUnregisterRequest {
    id: string;
}

// profile types

export class SetThumbnailRequest {
    thumbnail_id?: string;
}

export class AccountEditRequest {
    color?: string;
    thumbnail_id?: string;
    email?: string;
    password?: string;
    email_verification_code?: string;
}

export interface ProfileFollowRequest {
    follow?: boolean,
}

export interface UserAvatar {
    id: number;
    username: string;
    color: string;
    contrast_color: string;
    avatar_url: string | null;
}

export interface UserProfile {
    avatar: UserAvatar;
    is_following: boolean;
    is_followed_by: boolean;
    friends: UserAvatar[];
    followers: UserAvatar[];
    common_friends: UserAvatar[];
    potential_relationships: UserAvatar[];
}

// group types

export interface GroupAvatar {
    id: string;
    name: string;
    color: string;
    contrast_color: string;
    avatar_url: string | null;
}

// picture types

export enum ThumbnailSize {
    CARD = "card",
    AVATAR = "avatar",
}

export class CropRequest {
    picture_id!: string;
    x!: number;
    y!: number;
    scale!: number;
    size_type!: ThumbnailSize;
}

export class CropResponse {
    thumbnail_id!: string;
    image_url!: string;
}

export class UploadResponse {
    picture_id!: string;
    image_url!: string;
}

// event types

export enum Participating {
    HOST = "host",
    MODERATOR = "moderator",
    ATTENDING = "attending",
    FOLLOWING = "following",
    INVITED = "invited",
    NOTATTENDING = "not_attending",
    NOTINVITED = "not_invited",
    SOLICITOR = "solicitor",
    BLOCKED = "blocked",
}

export class EditableEvent {
    event!: EventHappening;
    picture_id!: string | null;
    original_image_url!: string;
}

export class EditEventRequest {
    publish?: boolean;
    notify?: boolean;
    title?: string;
    color?: string;
    location?: string | null;
    info?: string | null;
    start_date?: string;
    start_time?: string;
    end_date?: string | null;
    end_time?: string | null;
    thumbnail_id?: string;
    access_level?: AccessLevel;
    open_posting?: boolean;
    open_invite?: boolean;
}

export interface EventRoles {
    is_host: boolean;
    is_moderator: boolean;
    is_blocked: boolean;
    can_post: boolean;
    can_invite: boolean;
}

export interface BlockParticipantRequest {
    user_id: number;
}

export interface EditParticipantRequest {
    user_id: number;
    roles: EventRoles;
}

export class EventInviteRequest {
    user_ids: number[];
    group_ids: string[];

    constructor(user_ids: number[], group_ids: string[]) {
        this.user_ids = user_ids;
        this.group_ids = group_ids;
    }
}

export interface EventHappening {
    event_id: string;
    url: string;
    title: string;
    start: string;
    end: string | null;
    image_url: string | null;
    location: string | null;
    info: string | null;
    color: string;
    contrast_color: string;
    open_posting: boolean;
    open_invite: boolean;
    is_following: boolean;
    invited_by: UserAvatar | null;
    participation_status: Participating;
    access_level: AccessLevel;
    permissions: EventPermissions;
    participants: EventParticipant[];
}

export interface EventPermissions {
    edit: boolean;
    moderate: boolean;
    invite: boolean;
}

export type AccessLevel = "public" | "private" | "secret";

export interface EventParticipant {
    avatar: UserAvatar;
    status: Participating;
    has_relation: boolean;
    can_post: boolean;
    can_invite: boolean;
}

export type SearchFilter = "any" | "owned";

export class SearchRequest {
    query?: string;
    filter?: SearchFilter;

    constructor(query: string | undefined, filter: string | undefined) {
        this.query = query;
        this.filter = filter as SearchFilter;
    }

    urlParams() {
        let parms = new URLSearchParams();
        if (!!this.query) {
            parms.set("query", this.query);
        }
        if (!!this.filter) {
            parms.set("filter", this.filter);
        }
        return parms;
    }
}